/**
 * Mehrspieler-Synchronisation (Grundlage, Modus „coop“: alle steuern dieselbe Regierung).
 *
 * Prinzip: Alle Clients starten mit demselben Seed. Kein Zug wird lokal angewendet; jeder
 * Zug geht als Zeile in `moves`, bekommt dort serverseitig seine laufende Nummer und kommt
 * per Realtime an alle zurück. Weil die Engine deterministisch ist, sind danach alle
 * Zustände identisch. Lücken (verpasste Zeilen) werden per Nachladen geschlossen.
 */
import { tooNew, verOf } from '../../engine/version';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { useGame, setMoveSink, setStorageScope, setRemoteN, setLocalRole } from '../../store/game';
import { replay } from '../../engine/replay';
import { journal, type Action } from '../../engine/actions';
import { listMoves, sendMove, type Match, type MoveRow } from './api';

export type Role = 'host' | 'guest';
export type SyncStatus = { match: Match; role: Role; applied: number; pending: number; error: string | null };

let channel: RealtimeChannel | null = null;
let current: Match | null = null;
let me: string | null = null;
const roleOf = (uid: string): Role => current && uid === current.host ? 'host' : 'guest';
export const myRole = (): Role | null => current && me ? roleOf(me) : null;
let applied = 0;
let pendingSent = 0;
let listeners: ((s: SyncStatus | null) => void)[] = [];
let queue: MoveRow[] = [];
let catchingUp = false;
let lastError: string | null = null;
let sendChain: Promise<unknown> = Promise.resolve();
let poller: ReturnType<typeof setInterval> | null = null;
const MATCH_KEY = 'kabinett21-match';

function emit() { const s = status(); listeners.forEach((l) => l(s)); }
export function status(): SyncStatus | null { return current ? { match: current, role: myRole() ?? 'guest', applied, pending: pendingSent, error: lastError } : null; }
export function onStatus(l: (s: SyncStatus | null) => void) { listeners.push(l); return () => { listeners = listeners.filter((x) => x !== l); }; }

/**
 * Der handelnde Spieler eines Zugs ergibt sich aus dem Absender in der Datenbank, nicht aus dem
 * Zug selbst. Welche Züge ihm gerade zustehen, prüft die Engine (allowedFor).
 */
const asOf = (r: MoveRow) => roleOf(r.actor);

function applyRow(r: MoveRow) {
  if (r.n !== applied + 1) return false;
  useGame.getState().applyRemote(r.action, r.n, asOf(r));
  applied = r.n;
  if (pendingSent > 0) pendingSent--;
  return true;
}

/**
 * Was jede Partie dieses Modus vor dem ersten Zug braucht. Wird bei allen Clients lokal angewendet,
 * statt als Zug verschickt zu werden: so kann es nicht verloren gehen.
 */
function preamble(m: Match): Action[] { return m.mode === 'versus' ? [{ type: 'mpStart', args: [] }] : []; }

/** Holt alle Züge nach, die per Realtime nicht angekommen sind. Läuft zusätzlich alle paar Sekunden. */
async function catchUp() {
  if (!current || catchingUp) return;
  catchingUp = true;
  try { const rows = await listMoves(current.id, applied); rows.forEach(applyRow); queue = queue.filter((r) => r.n > applied); }
  catch (e) { lastError = (e as Error).message; }
  finally { catchingUp = false; }
  emit();
}

/** Wendet gepufferte Züge in Reihenfolge an; bei Lücken werden fehlende Zeilen nachgeladen. */
async function drain() {
  if (!current || catchingUp) return;
  queue.sort((a, b) => a.n - b.n);
  while (queue.length && queue[0].n <= applied) queue.shift();
  while (queue.length && queue[0].n === applied + 1) applyRow(queue.shift()!);
  if (queue.length && queue[0].n > applied + 1) {
    catchingUp = true;
    try { const rows = await listMoves(current.id, applied); rows.forEach(applyRow); } finally { catchingUp = false; }
    queue = queue.filter((r) => r.n > applied);
    if (queue.length) await drain();
  }
  emit();
}

/**
 * Verbindet diesen Client mit einer Partie: Zustand aus Seed + bisherigen Zügen aufbauen,
 * dann auf neue Züge hören und eigene Züge an den Server leiten.
 */
/** Hinweis, wenn eine Partie nach neueren Regeln läuft, als dieser (zwischengespeicherte) Code kennt. */
export const UPDATE_MSG = 'Diese Partie läuft mit einer neueren Version von Kabinett. Bitte lade die Seite neu.';

export async function connect(match: Match): Promise<string | null> {
  if (!supabase) return 'Nicht verbunden.';
  if (tooNew(match)) return UPDATE_MSG;
  await disconnect(false);
  current = match; applied = 0; pendingSent = 0; queue = []; lastError = null;
  me = (await supabase.auth.getUser()).data.user?.id ?? null;
  setStorageScope('mp');
  try { localStorage.setItem(MATCH_KEY, JSON.stringify(match)); } catch { /* egal */ }

  const rows = await listMoves(match.id, 0);
  setLocalRole(roleOf(me ?? ''));
  replay(match.seed, [...preamble(match), ...rows.map((r) => ({ ...r.action, as: asOf(r) }))], verOf(match));
  applied = rows.length ? rows[rows.length - 1].n : 0;
  setRemoteN(applied);
  useGame.getState().bump();

  setMoveSink((a: Action) => {
    pendingSent++; emit();
    if (!me) { lastError = 'Nicht angemeldet.'; pendingSent = Math.max(0, pendingSent - 1); emit(); return; }
    const actor = me;
    // Züge strikt nacheinander senden, damit der Server sie in der Reihenfolge nummeriert, in der sie gemacht wurden.
    sendChain = sendChain.then(() => sendMove(match.id, actor, a)).then((r) => {
      if ('error' in r) { pendingSent = Math.max(0, pendingSent - 1); lastError = 'Zug nicht gespeichert: ' + r.error; emit(); return; }
      lastError = null;
      // Eigenen Zug sofort einsortieren, nicht erst auf das Echo über Realtime warten.
      if (current && current.id === match.id) { queue.push({ match_id: match.id, n: r.n, actor, action: a, created_at: '' }); void drain(); }
    }).catch((e) => { pendingSent = Math.max(0, pendingSent - 1); lastError = 'Zug nicht gespeichert: ' + (e as Error).message; emit(); });
  });
  poller = setInterval(() => { void catchUp(); }, 4000);

  channel = supabase.channel('match:' + match.id)
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'moves', filter: `match_id=eq.${match.id}` }, (payload) => {
      queue.push(payload.new as MoveRow); void drain();
    })
    .subscribe();
  emit();
  return null;
}

export async function disconnect(forget = true) {
  setMoveSink(null);
  if (poller) { clearInterval(poller); poller = null; }
  if (forget) try { localStorage.removeItem(MATCH_KEY); } catch { /* egal */ }
  if (channel) { await supabase?.removeChannel(channel); channel = null; }
  const was = current;
  current = null; me = null; applied = 0; pendingSent = 0; queue = []; lastError = null;
  setStorageScope('sp'); setLocalRole('host');
  if (was) useGame.getState().loadFromStorage(); // Einzelspieler-Stand zurückholen
  emit();
}

/** Das lokale Zugprotokoll der laufenden Mehrspieler-Partie (für Highscore-Einträge). */
export function matchJournal(): Action[] { return journal.slice(); }

/** Nach einem Neuladen der Seite: zuletzt verbundene Partie wieder aufnehmen. */
export async function resume(): Promise<void> {
  if (current || !supabase) return;
  let m: Match | null = null;
  try { const r = localStorage.getItem(MATCH_KEY); if (r) m = JSON.parse(r); } catch { /* egal */ }
  if (m && m.id) await connect(m);
}

// Entwicklungsserver: Beim Nachladen dieser Datei muss die alte Instanz aufhören (Kanal, Abfrage),
// sonst wendet sie jeden Zug ein zweites Mal an. Die neue Instanz verbindet sich mit derselben Partie.
if (import.meta.hot) {
  import.meta.hot.dispose((data) => {
    data.match = current;
    if (poller) { clearInterval(poller); poller = null; }
    if (channel) { void supabase?.removeChannel(channel); channel = null; }
    setMoveSink(null);
    current = null;
  });
  const prev = import.meta.hot.data?.match as Match | null | undefined;
  if (prev) void connect(prev);
}
